import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Upload, RotateCcw, Check, Trash2, Loader2, ImageIcon, Pencil, X } from "lucide-react";
import { api, API } from "@/lib/api";
import { setBrandVersion, useBrandLogo } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { LogoLightbox, UseLogoDialog } from "@/components/BrandingDialogs";

const fileSrc = (path) => `${API}/branding/file?path=${encodeURIComponent(path)}`;
const brDate = (iso) => iso ? iso.split("-").reverse().join("/") : "";

const MODELS = [
  { k: "gemini", label: "Gemini", hint: "edita o logo atual" },
  { k: "openai", label: "OpenAI", hint: "cria um logo novo" },
];
const err = (e, d) => e?.response?.data?.detail || d;

const History = ({ items, active, onUse, onDelete, onEdit, onZoom }) => (
  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="brand-history">
    {items.map((it) => (
      <div key={it.path} className={`relative rounded-xl border-2 p-1 bg-white ${it.path === active ? "border-[#33b679]" : "border-slate-100"}`}>
        <img src={fileSrc(it.path)} alt="" onClick={() => onZoom(fileSrc(it.path))} className="w-full aspect-square object-contain cursor-zoom-in" loading="lazy" />
        <button onClick={() => onEdit(it.path)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1 shadow text-[#7c3aed] hover:scale-110 transition-transform" aria-label="Ajustar com IA" data-testid="brand-history-edit"><Pencil className="w-3 h-3" /></button>
        {it.path === active
          ? <span className="absolute top-1 left-1 bg-[#33b679] text-white rounded-full p-0.5"><Check className="w-3 h-3" /></span>
          : (
            <div className="flex gap-1 mt-1">
              <button onClick={() => onUse(it.path)} className="flex-1 text-[10px] font-bold rounded bg-[#1e3a8a] text-white py-0.5" data-testid="brand-history-use">Usar</button>
              <button onClick={() => onDelete(it.path)} className="text-slate-400 hover:text-red-600" aria-label="Apagar" data-testid="brand-history-delete"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          )}
      </div>
    ))}
  </div>
);

export const BrandingCard = () => {
  const logo = useBrandLogo();
  const [model, setModel] = useState("gemini");
  const [prompt, setPrompt] = useState("");
  const [draft, setDraft] = useState(null);
  const [base, setBase] = useState(null);
  const [zoom, setZoom] = useState(null);
  const [using, setUsing] = useState(null);
  const [busy, setBusy] = useState("");
  const [hist, setHist] = useState({ active: "", items: [] });
  const fileRef = useRef(null);
  const loadHist = () => api.get("/branding/history").then((r) => setHist(r.data)).catch(() => {});
  useEffect(() => { loadHist(); }, []);

  const run = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); if (ok) toast.success(ok); } catch (e) { toast.error(err(e, "Não deu certo, tente de novo")); } finally { setBusy(""); }
  };
  const activate = (path, preview) => setUsing({ path, preview: preview || fileSrc(path) });
  const confirmUse = (dates) => run("use", async () => {
    const r = await api.post("/branding/activate", { path: using.path, ...dates });
    if (!r.data.scheduled) setBrandVersion(r.data.version);
    toast.success(r.data.scheduled ? `Logo agendado a partir de ${brDate(dates.start_date)}` : "Logo atualizado em orçamentos, PDFs, site e ícone do app");
    setUsing(null); setDraft(null); loadHist();
  });
  const cancelSchedule = () => run("del", async () => { await api.delete("/branding/schedule"); loadHist(); }, "Agendamento cancelado");
  const generate = () => run("ai", async () => {
    const r = await api.post("/branding/ai", { prompt, model, base_path: base?.path || null }, { timeout: 180000 });
    setDraft(r.data); setBase(null); setPrompt(""); loadHist();
  });
  const editFrom = (b) => {
    setBase(b);
    document.querySelector("[data-testid='brand-ai']")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const onFile = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    run("up", async () => {
      const fd = new FormData(); fd.append("file", f);
      const r = await api.post("/branding/upload", fd, { noOffline: true });
      setDraft(r.data); loadHist();
    });
  };
  const reset = () => run("reset", async () => {
    const r = await api.post("/branding/reset"); setBrandVersion(r.data.version); loadHist();
  }, "Logo original restaurado");
  const remove = (path) => run("del", async () => { await api.delete("/branding/history", { params: { path } }); loadHist(); });

  return (
    <section className="bg-white rounded-2xl border border-slate-100 card-shadow p-5 space-y-4" data-testid="branding-card">
      <h2 className="font-heading text-base font-bold text-[#1e3a8a] flex items-center gap-2"><ImageIcon className="w-5 h-5" />Logo da AMC</h2>
      <p className="text-xs text-slate-500 -mt-2">Vale nos orçamentos, PDFs, site público e no ícone do app.</p>
      <div className="flex items-center gap-4">
        <img src={logo} alt="Logo atual" onClick={() => setZoom(logo)} className="w-24 h-24 object-contain rounded-xl border border-slate-100 bg-white p-1 cursor-zoom-in" data-testid="brand-current" />
        <div className="flex flex-col gap-2">
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={!!busy} data-testid="brand-upload-btn"><Upload className="w-4 h-4 mr-1.5" />Enviar imagem</Button>
          {hist.active && <Button variant="outline" size="sm" onClick={reset} disabled={!!busy} data-testid="brand-reset"><RotateCcw className="w-4 h-4 mr-1.5" />Voltar ao original</Button>}
        </div>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} data-testid="brand-upload-input" />
      </div>
      {(hist.revert_at || hist.schedule) && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-900 flex items-center gap-2" data-testid="brand-schedule-info">
          <span className="flex-1">
            {hist.revert_at && <>Volta ao original depois de <b>{brDate(hist.revert_at)}</b>. </>}
            {hist.schedule && <>Agendado: outro logo de <b>{brDate(hist.schedule.start_date)}</b>{hist.schedule.end_date ? <> até <b>{brDate(hist.schedule.end_date)}</b></> : null}.</>}
          </span>
          <button onClick={cancelSchedule} className="font-bold underline" data-testid="brand-schedule-cancel">Cancelar</button>
        </div>
      )}

      <div className="rounded-2xl bg-gradient-to-br from-violet-50 to-blue-50 p-3 space-y-2" data-testid="brand-ai">
        <p className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-[#7c3aed]" />Pedir pra IA</p>
        {base ? (
          <div className="flex items-center gap-2 rounded-xl bg-white p-2" data-testid="brand-editing">
            <img src={base.preview} alt="" className="w-12 h-12 object-contain rounded-lg" />
            <p className="flex-1 text-[12px] text-slate-700"><b>Ajustando este logo.</b> Diga só o que mudar (ex.: “troque a data para 2026”).</p>
            <button onClick={() => setBase(null)} className="p-1 text-slate-400 hover:text-slate-700" aria-label="Cancelar ajuste" data-testid="brand-editing-cancel"><X className="w-4 h-4" /></button>
          </div>
        ) : (
        <div className="grid grid-cols-2 gap-2">
          {MODELS.map((m) => (
            <button key={m.k} type="button" onClick={() => setModel(m.k)} data-testid={`brand-model-${m.k}`}
              className={`rounded-xl border-2 px-2 py-1.5 text-left transition-colors ${model === m.k ? "border-[#7c3aed] bg-white" : "border-transparent bg-white/60"}`}>
              <span className="block text-[13px] font-bold">{m.label}</span><span className="block text-[10.5px] text-slate-500">{m.hint}</span>
            </button>
          ))}
        </div>
        )}
        <Textarea rows={2} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder={base ? "Ex.: corrija a data para 2026" : "Ex.: coloque um gorro de Papai Noel no A"} data-testid="brand-prompt" />
        <Button onClick={generate} disabled={!!busy || prompt.trim().length < 3} className="w-full bg-[#7c3aed] hover:bg-[#6d28d9] text-white" data-testid="brand-generate">
          {busy === "ai" ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Criando… (até 1 min)</> : <><Sparkles className="w-4 h-4 mr-1.5" />Gerar logo</>}
        </Button>
      </div>

      {draft && (
        <div className="rounded-2xl border-2 border-dashed border-[#7c3aed]/40 p-3 flex items-center gap-3" data-testid="brand-draft">
          <img src={draft.preview} alt="Prévia" onClick={() => setZoom(draft.preview)} className="w-28 h-28 object-contain rounded-xl bg-white cursor-zoom-in" data-testid="brand-draft-img" />
          <div className="flex-1 space-y-2">
            <p className="text-sm font-semibold">Gostou?</p>
            <Button size="sm" onClick={() => activate(draft.path, draft.preview)} disabled={!!busy} className="w-full bg-[#33b679] hover:bg-[#2a9d67] text-white" data-testid="brand-draft-use"><Check className="w-4 h-4 mr-1" />Usar este logo</Button>
            <Button size="sm" variant="outline" onClick={() => editFrom(draft)} disabled={!!busy} className="w-full border-[#7c3aed]/40 text-[#7c3aed]" data-testid="brand-draft-edit"><Pencil className="w-4 h-4 mr-1" />Ajustar este</Button>
            <Button size="sm" variant="outline" onClick={() => setDraft(null)} className="w-full" data-testid="brand-draft-discard">Descartar</Button>
          </div>
        </div>
      )}

      {hist.items.length > 0 && (
        <div className="space-y-2">
          <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wide">Logos salvos (ex.: Natal)</p>
          <History items={hist.items} active={hist.active} onUse={activate} onDelete={remove} onZoom={setZoom}
            onEdit={(path) => editFrom({ path, preview: fileSrc(path) })} />
        </div>
      )}
      <LogoLightbox src={zoom} onOpenChange={(v) => !v && setZoom(null)} />
      <UseLogoDialog logo={using} busy={busy === "use"} onOpenChange={(v) => !v && setUsing(null)} onConfirm={confirmUse} />
    </section>
  );
};
